import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { beginRoutePreload, hideRoutePreloader } from '../components/loading/dismissAppBoot';
import { reloadIfDeployStale } from '../utils/assetCache';

const FADE_IN_MS = 180;

export function buildTransitionKey(page, extras = {}) {
  if (page === 'casino') return `casino:${extras.casinoGameId || 'lobby'}`;
  if (page === 'account') return 'account';
  if (page === 'help') return `help:${extras.helpPath || 'main'}`;
  if (page === 'cms') return `cms:${extras.cmsSlug || 'page'}`;
  if (page === 'promos') return `promos:${extras.promosKind || 'main'}`;
  return page;
}

export function parseTransitionKey(transitionKey) {
  const raw = String(transitionKey || 'home');
  const [page, detail] = raw.split(':');
  if (page === 'casino') {
    return {
      page: 'casino',
      casinoGameId: detail && detail !== 'lobby' ? detail : null,
    };
  }
  return { page, casinoGameId: null };
}

function prefersReducedMotion() {
  try {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  } catch {
    return false;
  }
}

function pageFamily(transitionKey) {
  return String(transitionKey || 'home').split(':')[0];
}

export function usePageTransition(transitionKey) {
  const [displayKey, setDisplayKey] = useState(transitionKey);
  const [phase, setPhase] = useState('idle');
  const displayKeyRef = useRef(displayKey);
  const inTimerRef = useRef(0);
  const routeCtlRef = useRef(null);

  useEffect(() => {
    displayKeyRef.current = displayKey;
  }, [displayKey]);

  useEffect(() => () => {
    window.clearTimeout(inTimerRef.current);
    if (routeCtlRef.current) {
      routeCtlRef.current.cancel();
      routeCtlRef.current = null;
    }
  }, []);

  useLayoutEffect(() => {
    if (transitionKey === displayKeyRef.current) return undefined;

    const fromFamily = pageFamily(displayKeyRef.current);
    const toFamily = pageFamily(transitionKey);
    const sameFamily = fromFamily === toFamily;
    const isMajorPage = fromFamily !== toFamily;
    const toCasinoGame = toFamily === 'casino'
      && String(transitionKey).startsWith('casino:')
      && !String(transitionKey).endsWith(':lobby');
    const skipFade = toFamily === 'vip'
      || fromFamily === 'vip'
      || toCasinoGame
      || (sameFamily && (fromFamily === 'account' || fromFamily === 'help' || fromFamily === 'cms'));

    window.clearTimeout(inTimerRef.current);
    if (routeCtlRef.current) {
      routeCtlRef.current.cancel();
      routeCtlRef.current = null;
    }

    // Switch displayed page before paint so CMS cannot flash 404 while slug clears.
    setDisplayKey(transitionKey);

    if (isMajorPage && !toCasinoGame && !prefersReducedMotion()) {
      const ctl = beginRoutePreload();
      routeCtlRef.current = ctl;
      setPhase('idle');

      let cancelled = false;
      (async () => {
        const reloading = await reloadIfDeployStale();
        if (cancelled || reloading) return;
        if (cancelled) return;
        ctl.finish();
        if (routeCtlRef.current === ctl) routeCtlRef.current = null;
        setPhase('in');
        inTimerRef.current = window.setTimeout(() => {
          setPhase('idle');
        }, FADE_IN_MS);
      })();

      return () => {
        cancelled = true;
        window.clearTimeout(inTimerRef.current);
      };
    }

    hideRoutePreloader();

    if (skipFade || prefersReducedMotion()) {
      setPhase('idle');
      return undefined;
    }

    setPhase('in');
    inTimerRef.current = window.setTimeout(() => {
      setPhase('idle');
    }, FADE_IN_MS);

    return () => {
      window.clearTimeout(inTimerRef.current);
    };
  }, [transitionKey]);

  const pageBodyClass = useMemo(() => {
    if (phase === 'in') return 'page-body--fading-in';
    return '';
  }, [phase]);

  const display = useMemo(() => parseTransitionKey(displayKey), [displayKey]);

  return {
    displayKey,
    displayPage: display.page,
    displayCasinoGameId: display.casinoGameId,
    pageBodyClass,
  };
}
